import { ArrowLeft, Building2, Eye, EyeOff } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PASSWORD_SIGN_IN_AVAILABLE, signInWithPassword } from '@/auth/passwordSignIn';
import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { PRIVACY_POLICY, TERMS_OF_USE } from '@/legal/documents';
import { readLastStudioSignIn } from '@/lib/studioIdentity';
import { SignInArtwork } from '@/pages/SignIn/SignInArtwork';

type Step = 'password' | 'sso';

type Problem = 'wrong_credentials' | 'too_many_attempts' | 'failed';

const PROBLEM_MESSAGE: Record<Problem, string> = {
  wrong_credentials: 'auth.wrongCredentials',
  too_many_attempts: 'auth.tooManyAttempts',
  failed: 'auth.errorBody',
};

/**
 * Studio's sign-in: email and password first, single sign-on one step away.
 * The SSO step takes only the work email and sends the person to their
 * organization's sign-in. A password for an organization that uses SSO, or
 * one WorkOS needs another step for (MFA, verifying the email), carries on
 * there too, with the email filled in.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const lastSignIn = useMemo(() => readLastStudioSignIn(), []);
  const [step, setStep] = useState<Step>(PASSWORD_SIGN_IN_AVAILABLE ? 'password' : 'sso');
  const [email, setEmail] = useState(lastSignIn?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const redirectToSignIn = (loginHint: string | undefined) => {
    setSubmitting(true);
    void loginWithRedirect({
      authorizationParams: loginHint ? { login_hint: loginHint } : {},
    }).catch(() => {
      setProblem('failed');
      setSubmitting(false);
    });
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setProblem(null);
    const outcome = await signInWithPassword(email, password);
    if (outcome.kind === 'signed_in') {
      window.location.reload();
      return;
    }
    if (outcome.kind === 'redirect') {
      redirectToSignIn(email.trim());
      return;
    }
    setProblem(outcome.kind);
    setSubmitting(false);
  };

  const submitSso = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setProblem(null);
    redirectToSignIn(email.trim() || undefined);
  };

  const goTo = (next: Step) => {
    setProblem(null);
    setStep(next);
  };

  return (
    <main className="relative grid min-h-dvh w-full overflow-hidden bg-background lg:grid-cols-[minmax(0,1.15fr)_minmax(480px,1fr)]">
      <SignInArtwork />

      <section className="relative z-10 flex min-h-dvh flex-col px-6 py-8 sm:px-12 lg:col-start-2 lg:px-16" aria-labelledby="studio-sign-in-title">
        <div className="flex items-center gap-3 text-foreground">
          <RapidSOSMark className="h-6 w-6" animation={DEFAULT_MARK_ANIMATION} />
          <RapidSOSWordmark className="h-6" />
          <span className="h-5 border-l border-border" aria-hidden="true" />
          <span className="text-sm font-medium text-muted-foreground">Studio</span>
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-12">
          {step === 'password' ? (
            <>
              <h1 id="studio-sign-in-title" className="text-3xl font-semibold tracking-tight text-foreground">
                {t('auth.signInTitle')}
              </h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{t('auth.passwordSubtitle')}</p>

              <form onSubmit={(event) => void submitPassword(event)} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="studio-email" className="text-sm font-medium">{t('auth.workEmail')}</Label>
                  <Input
                    id="studio-email"
                    type="email"
                    autoComplete="username"
                    required
                    disabled={submitting}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    className="mt-2 h-11"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="studio-password" className="text-sm font-medium">{t('auth.password')}</Label>
                    <button
                      type="button"
                      onClick={() => redirectToSignIn(email.trim() || undefined)}
                      className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative mt-2">
                    <Input
                      id="studio-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      disabled={submitting}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="h-11 pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((shown) => !shown)}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {problem ? <p role="alert" className="text-sm text-destructive">{t(PROBLEM_MESSAGE[problem])}</p> : null}
                <Button type="submit" className="h-11 w-full" disabled={submitting} aria-busy={submitting}>
                  {submitting ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </form>

              <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                {t('auth.or')}
                <span className="h-px flex-1 bg-border" />
              </div>

              <Button type="button" variant="outline" className="h-11 w-full gap-2" disabled={submitting} onClick={() => goTo('sso')}>
                <Building2 className="h-4 w-4" aria-hidden="true" />
                {t('auth.continueWithSso')}
              </Button>
            </>
          ) : (
            <>
              {PASSWORD_SIGN_IN_AVAILABLE ? (
                <button
                  type="button"
                  onClick={() => goTo('password')}
                  className="mb-8 inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  {t('auth.backToPassword')}
                </button>
              ) : null}
              <h1 id="studio-sign-in-title" className="text-3xl font-semibold tracking-tight text-foreground">
                {t('auth.ssoTitle')}
              </h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{t('auth.ssoStepSubtitle')}</p>

              <form onSubmit={submitSso} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="studio-sso-email" className="text-sm font-medium">{t('auth.workEmail')}</Label>
                  <Input
                    id="studio-sso-email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    disabled={submitting}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    className="mt-2 h-11"
                  />
                </div>
                {problem ? <p role="alert" className="text-sm text-destructive">{t(PROBLEM_MESSAGE[problem])}</p> : null}
                <Button type="submit" className="h-11 w-full" disabled={submitting} aria-busy={submitting}>
                  {submitting ? t('auth.connecting') : t('auth.continue')}
                </Button>
              </form>
              <p className="mt-4 text-xs leading-5 text-muted-foreground">{t('auth.ssoStepNote')}</p>
            </>
          )}
        </div>

        <footer className="space-y-2 text-center text-xs leading-5 text-muted-foreground">
          <p>{t('auth.accessManaged')}</p>
          <p>
            <Trans
              i18nKey="legal.signInNotice"
              components={{
                terms: <Link to={TERMS_OF_USE.path} className="underline underline-offset-2 hover:text-foreground" />,
                privacy: <Link to={PRIVACY_POLICY.path} className="underline underline-offset-2 hover:text-foreground" />,
              }}
            />
          </p>
        </footer>
      </section>
    </main>
  );
}
